// src/pages/Inbox.tsx
import React from 'react'
import DOMPurify from 'dompurify'
import { Link, useSearchParams } from 'react-router-dom'
import { Inbox as InboxIcon, Calendar, CalendarPlus, CheckCircle2, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown, MessageSquare, FileText, DollarSign, Star, Sparkles, Wand2 } from 'lucide-react'
import { InboxDetailContent } from './InboxDetail'
import NotesRichEditor from '../components/NotesRichEditor'
import AddBookingSheet from '../components/AddBookingSheet'
import { RatingStarsDisplay } from '../components/RatingStars'
import {
  api,
  atlas,
  doctorMe,
  educationMe,
  techMe,
  propertyMe,
  financialMe,
  alliedMe,
  legalMe,
  migrationMe,
  ProviderProfession,
  isStackEnabled,
  hasActiveProviderProfile,
} from '../api'
import Spinner from '../components/Spinner'
import { detectTimeZone } from '../countryRules'

type PatientSummary = {
  ageYears?: number | null
  gender?: string | null
  state?: string | null
} | null

type NoteAttachment = {
  key: string
  name?: string | null
  type?: string | null
  size?: number | null
  kind?: 'image' | 'file' | null
  uploadedAt?: string | null
}

type IncomingItem = {
  selectionId: string
  specialistId: string
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'LOST' | 'CANCELLED'
  slotISO: string
  createdAt: string
  feeCapCents: number | null
  specialty: string
  reason: string
  patientName?: string | null
  patientContact?: {
    name?: string | null
    email?: string | null
    phone?: string | null
  } | null
  selectionCreatedAt?: string | null
  patientSummary?: PatientSummary
  patientId?: string
  clientKey?: string | null
  notes?: string | null
  notesUpdatedAt?: string | null
  notesSummary?: string | null
  notesSummaryUpdatedAt?: string | null
  notesPublishedAt?: string | null
  notesAttachments?: NoteAttachment[] | null
  paymentStatus?: string | null
  invoiceStatus?: string | null
  meeting?: {
    provider?: string | null
    joinUrl?: string | null
    hostUrl?: string | null
    meetingId?: string | null
    createdAt?: string | null
    sentAt?: string | null
    meetingTimeISO?: string | null
    meetingTimeZone?: string | null
    status?: string | null
  } | null
  review?: {
    client?: {
      submitted?: boolean
    }
  } | null
  reviewReceived?: {
    submitted?: boolean
    rating?: number | null
    comment?: string | null
    createdAt?: string | null
    reviewerFirstName?: string | null
    providerReply?: {
      comment?: string | null
      createdAt?: string | null
      updatedAt?: string | null
      replierName?: string | null
      replierId?: string | null
    } | null
  } | null
}
type StripeStatus = {
  connected: boolean
  accountType?: string | null
  chargesEnabled?: boolean
  payoutsEnabled?: boolean
  detailsSubmitted?: boolean
  hasAccessToken?: boolean
  requirements?: {
    currentlyDue?: string[]
    pendingVerification?: string[]
    disabledReason?: string | null
  }
}
const CONTACT_SLOT = 'REQUEST_BOOKING'
const MAX_NOTES_FILE_MB = 30
const MAX_NOTES_FILE_BYTES = MAX_NOTES_FILE_MB * 1024 * 1024
const MAX_AI_SUMMARY_CHARS = 1200
const MAX_AI_DRAFT_INPUT_CHARS = (() => {
  const raw =
    import.meta.env.VITE_AI_DRAFT_INPUT_MAX_CHARS
      || import.meta.env.VITE_AI_SUMMARY_INPUT_MAX_CHARS
      || '6000'
  const parsed = Number.parseInt(raw, 10)
  return Number.isFinite(parsed) ? parsed : 6000
})()
const NOTE_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/webp'])
const NOTE_FILE_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain',
])
const NOTE_FILE_EXTS = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt'])
const DEFAULT_REVIEW_REMINDER_MESSAGE =
  'Thanks again for your booking. If you have a moment, I’d really appreciate a quick review.'

async function uploadToS3(presign: { url: string; fields: Record<string, string> }, file: File) {
  const form = new FormData()
  Object.entries(presign.fields || {}).forEach(([k, v]) => {
    form.append(k, v)
  })
  form.append('file', file)
  const res = await fetch(presign.url, { method: 'POST', body: form })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || 'Upload failed')
  }
}

const formatBytes = (bytes?: number | null) => {
  if (!Number.isFinite(bytes || 0)) return ''
  const value = Number(bytes)
  if (value <= 0) return ''
  if (value < 1024) return `${Math.round(value)} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}
const normalizeEmail = (value?: string | null) => (value || '').trim().toLowerCase()
const normalizePhone = (value?: string | null) => (value || '').replace(/[^0-9+]/g, '')
const normalizeName = (value?: string | null) =>
  (value || '').trim().toLowerCase().replace(/\s+/g, ' ')
const getClientKey = (item: IncomingItem) => {
  if (item.clientKey) return item.clientKey
  const rawId =
    item.patientId ||
    (item as any).patientUserId ||
    (item as any).clientId ||
    null
  if (rawId) return `id:${rawId}`
  const email = normalizeEmail(item.patientContact?.email)
  const phone = normalizePhone(item.patientContact?.phone)
  if (email && phone) return `ep:${email}|${phone}`
  if (email) return `e:${email}`
  if (phone) return `p:${phone}`
  const name = normalizeName(item.patientName || item.patientContact?.name)
  if (name) return `n:${name}`
  return null
}
const slotExpired = (iso?: string) => {
  if (!iso || iso === CONTACT_SLOT) return false
  const ts = Date.parse(iso)
  if (!Number.isFinite(ts)) return false
  return ts <= Date.now()
}
const isNotesEligible = (item: IncomingItem) => {
  const status = String(item.status || '').toUpperCase()
  if (status === 'ACCEPTED') return true
  if (status === 'PENDING') return !slotExpired(item.slotISO)
  return false
}
const RAW_PROVIDER_STACKS: Array<{
  id: ProviderProfession
  label: string
  registerPath: string
  fetchProfile: () => Promise<any>
}> = [
  { id: 'doctor', label: 'Medical Doctors', registerPath: '/professionals/doctors', fetchProfile: doctorMe },
  { id: 'tech', label: 'Tech & Creative', registerPath: '/professionals/tech', fetchProfile: techMe },
  { id: 'property', label: 'Property, Design & Building Services', registerPath: '/professionals/property', fetchProfile: propertyMe },
  { id: 'financial', label: 'Financial Advisory', registerPath: '/professionals/financial', fetchProfile: financialMe },
  { id: 'allied', label: 'Allied Health', registerPath: '/professionals/allied', fetchProfile: alliedMe },
  { id: 'legal', label: 'Legal & Document Services', registerPath: '/professionals/legal', fetchProfile: legalMe },
  { id: 'migration', label: 'Migration & Visa', registerPath: '/professionals/migration', fetchProfile: migrationMe },
  { id: 'education', label: 'Education & Tutoring', registerPath: '/professionals/education', fetchProfile: educationMe },
]
const PROVIDER_STACKS = (() => {
  const enabled = RAW_PROVIDER_STACKS.filter(stack => isStackEnabled(stack.id))
  return enabled.length ? enabled : RAW_PROVIDER_STACKS
})()

export default function Inbox() {
  const [items, setItems] = React.useState<IncomingItem[]>([])
  const [loading, setLoading] = React.useState(true)
  const [err, setErr] = React.useState<string|null>(null)
  const [needsProviderProfile, setNeedsProviderProfile] = React.useState(false)
  const [ready, setReady] = React.useState(false)
  const [activeTab] = React.useState<'requests'>('requests')
  const [stackProfiles, setStackProfiles] = React.useState<Record<string, any>>({})
  const [stackStatuses, setStackStatuses] = React.useState<Record<string, 'unknown' | 'approved' | 'unapproved'>>({})
  const [activeStack, setActiveStack] = React.useState<ProviderProfession>('doctor')
  const [statusFilter, setStatusFilter] = React.useState<'confirmed' | 'clients'>('confirmed')
  const [notesOpen, setNotesOpen] = React.useState(false)
  const [notesActiveId, setNotesActiveId] = React.useState<string | null>(null)
  const [notesFilterKey, setNotesFilterKey] = React.useState<string | null>(null)
  const [notesDrafts, setNotesDrafts] = React.useState<Record<string, string>>({})
  const [notesSavedAt, setNotesSavedAt] = React.useState<Record<string, string | null>>({})
  const [notesSaving, setNotesSaving] = React.useState<Record<string, boolean>>({})
  const [notesSummaryDrafts, setNotesSummaryDrafts] = React.useState<Record<string, string>>({})
  const [notesSummarySavedAt, setNotesSummarySavedAt] = React.useState<Record<string, string | null>>({})
  const [notesSummaryEditing, setNotesSummaryEditing] = React.useState<Record<string, boolean>>({})
  const [notesSummaryGenerating, setNotesSummaryGenerating] = React.useState<Record<string, boolean>>({})
  const [notesSummarySaving, setNotesSummarySaving] = React.useState<Record<string, boolean>>({})
  const [notesSummaryErrors, setNotesSummaryErrors] = React.useState<Record<string, string | null>>({})
  const [notesDraftPreview, setNotesDraftPreview] = React.useState<Record<string, string>>({})
  const [notesDraftGenerating, setNotesDraftGenerating] = React.useState<Record<string, boolean>>({})
  const [notesDraftErrors, setNotesDraftErrors] = React.useState<Record<string, string | null>>({})
  const [notesError, setNotesError] = React.useState<string | null>(null)
  const [notesAttachmentUrls, setNotesAttachmentUrls] = React.useState<Record<string, string>>({})
  const [viewOpen, setViewOpen] = React.useState(false)
  const [viewSelectionId, setViewSelectionId] = React.useState<string | null>(null)
  const [paymentOpen, setPaymentOpen] = React.useState(false)
  const [paymentSelectionId, setPaymentSelectionId] = React.useState<string | null>(null)
  const [meetingOpen, setMeetingOpen] = React.useState(false)
  const [meetingSelectionId, setMeetingSelectionId] = React.useState<string | null>(null)
  const [reviewOpen, setReviewOpen] = React.useState(false)
  const [reviewSelectionId, setReviewSelectionId] = React.useState<string | null>(null)
  const [reviewReminderOpen, setReviewReminderOpen] = React.useState(false)
  const [reviewReminderSelectionId, setReviewReminderSelectionId] = React.useState<string | null>(null)
  const [reviewReplyDraft, setReviewReplyDraft] = React.useState('')
  const [reviewReplyBusy, setReviewReplyBusy] = React.useState(false)
  const [reviewReplyError, setReviewReplyError] = React.useState<string | null>(null)
  const [reviewReplyNotice, setReviewReplyNotice] = React.useState<string | null>(null)
  const [reviewReminderMessage, setReviewReminderMessage] = React.useState('')
  const [reviewReminderSaveDefault, setReviewReminderSaveDefault] = React.useState(false)
  const [reviewReminderSending, setReviewReminderSending] = React.useState(false)
  const [reviewReminderError, setReviewReminderError] = React.useState<string | null>(null)
  const [reviewReminderSuccess, setReviewReminderSuccess] = React.useState<string | null>(null)
  const [reviewReminderDirty, setReviewReminderDirty] = React.useState(false)
  const [addBookingOpen, setAddBookingOpen] = React.useState(false)
  const [stripeStatus, setStripeStatus] = React.useState<StripeStatus | null>(null)
  const [stripeStatusLoading, setStripeStatusLoading] = React.useState(false)
  const [stripeStatusError, setStripeStatusError] = React.useState<string | null>(null)
  const [sortKey, setSortKey] = React.useState<'date' | 'name' | 'payment'>('date')
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc')
  const [clientCursor, setClientCursor] = React.useState<string | null>(null)
  const [clientLoadingMore, setClientLoadingMore] = React.useState(false)
  const [chatUnreadSignals, setChatUnreadSignals] = React.useState<Record<string, number>>({})
  const [searchParams] = useSearchParams()
  const notesParamHandled = React.useRef<string | null>(null)
  const meetingParamHandled = React.useRef<string | null>(null)
  const stackParamHandled = React.useRef<string | null>(null)
  const reviewParamHandled = React.useRef<string | null>(null)
  const previousStackRef = React.useRef<ProviderProfession>('doctor')
  const fetchedStacks = React.useRef<Set<ProviderProfession>>(new Set())
  const notesSaveTimers = React.useRef<Record<string, number>>({})
  const itemsRef = React.useRef<IncomingItem[]>([])
  const notesDraftsRef = React.useRef<Record<string, string>>({})
  const overlayOpenRef = React.useRef(false)
  const overlayStateRef = React.useRef(false)
  const closeByPopRef = React.useRef(false)
  const ignorePopRef = React.useRef(false)
  const availableStacks = React.useMemo(
    () => PROVIDER_STACKS.filter((stack) => stackStatuses[stack.id] !== 'unapproved'),
    [stackStatuses]
  )
  const activeStackMeta = React.useMemo(
    () =>
      availableStacks.find(stack => stack.id === activeStack) ||
      availableStacks[0] ||
      PROVIDER_STACKS[0],
    [activeStack, availableStacks]
  )
  const reviewReminderDefaultMessage = React.useMemo(() => {
    const saved =
      stackProfiles[activeStack]?.reviewReminderMessage ||
      stackProfiles[activeStack]?.profile?.reviewReminderMessage
    if (typeof saved === 'string' && saved.trim()) return saved.trim()
    return DEFAULT_REVIEW_REMINDER_MESSAGE
  }, [stackProfiles, activeStack])

  React.useEffect(() => {
    if (!reviewReminderOpen) return
    if (reviewReminderDirty) return
    if (reviewReminderMessage !== reviewReminderDefaultMessage) {
      setReviewReminderMessage(reviewReminderDefaultMessage)
    }
  }, [reviewReminderOpen, reviewReminderDirty, reviewReminderMessage, reviewReminderDefaultMessage])

  const loadStackProfile = React.useCallback(
    async (stackId: ProviderProfession) => {
      if (fetchedStacks.current.has(stackId)) return
      fetchedStacks.current.add(stackId)
      const meta = PROVIDER_STACKS.find((s) => s.id === stackId)
      if (!meta) return
      try {
        const profile = await meta.fetchProfile().catch(() => null)
        if (profile && hasActiveProviderProfile(profile)) {
          setStackProfiles((prev) => ({ ...prev, [stackId]: profile }))
          setStackStatuses((prev) => ({ ...prev, [stackId]: 'approved' }))
        } else {
          setStackProfiles((prev) => ({ ...prev, [stackId]: { needsRegistration: true } }))
          setStackStatuses((prev) => ({ ...prev, [stackId]: 'unapproved' }))
        }
      } catch {
        setStackProfiles((prev) => ({ ...prev, [stackId]: { needsRegistration: true } }))
        setStackStatuses((prev) => ({ ...prev, [stackId]: 'unapproved' }))
      }
    },
    []
  )

  const loadStripeStatus = React.useCallback(async () => {
    setStripeStatusLoading(true)
    setStripeStatusError(null)
    try {
      const res = await api.stripeStatus()
      setStripeStatus(res || null)
    } catch (err: any) {
      setStripeStatusError(err?.message || 'Failed to load Stripe status')
    } finally {
      setStripeStatusLoading(false)
    }
  }, [])

  const applyNotesFromItems = React.useCallback((list: IncomingItem[], { overwrite = false } = {}) => {
    setNotesDrafts((prev) => {
      const next = { ...prev }
      for (const item of list) {
        const key = item.selectionId
        if (!key) continue
        if (overwrite || !(key in next)) {
          next[key] = item.notes || ''
        }
      }
      return next
    })
    setNotesSavedAt((prev) => {
      const next = { ...prev }
      for (const item of list) {
        const key = item.selectionId
        if (!key) continue
        if (overwrite || !(key in next)) {
          next[key] = item.notesUpdatedAt || null
        }
      }
      return next
    })
    setNotesSummaryDrafts((prev) => {
      const next = { ...prev }
      for (const item of list) {
        const key = item.selectionId
        if (!key) continue
        if (overwrite || !(key in next)) {
          next[key] = item.notesSummary || ''
        }
      }
      return next
    })
    setNotesSummarySavedAt((prev) => {
      const next = { ...prev }
      for (const item of list) {
        const key = item.selectionId
        if (!key) continue
        if (overwrite || !(key in next)) {
          next[key] = item.notesSummaryUpdatedAt || null
        }
      }
      return next
    })
  }, [])

  const load = React.useCallback(async () => {
    const status = stackStatuses[activeStack] || 'unknown'
    if (status === 'unknown') {
      setReady(false)
      return
    }
    if (status !== 'approved') {
      setItems([])
      setClientCursor(null)
      setNeedsProviderProfile(true)
      setErr(null)
      setReady(true)
      setLoading(false)
      return
    }
    setLoading(true); setErr(null); setReady(false)
    try {
      const res = await atlas.getIncomingSelections(activeStack, { includeAll: true })
      const list = (res?.items ?? []) as IncomingItem[]
      setItems(list)
      applyNotesFromItems(list)
      setClientCursor(res?.nextCursor || null)
      setNeedsProviderProfile(false)
      setReady(true)
    } catch (e:any) {
      if (String(e?.message || '').toLowerCase().includes('forbidden')) {
        setNeedsProviderProfile(true)
        setErr(null)
        setReady(true)
      } else {
        setErr(e?.message || 'Failed to load dashboard')
        setReady(true)
      }
    } finally {
      setLoading(false)
    }
  }, [activeStack, stackStatuses, applyNotesFromItems])

  const loadUnreadBySelection = React.useCallback(async () => {
    const next: Record<string, number> = {}
    const bump = (key: string, unread: number) => {
      if (!key) return
      if (!Number.isFinite(unread) || unread <= 0) return
      next[key] = (next[key] || 0) + Math.floor(unread)
    }
    try {
      let cursor: string | null = null
      let pages = 0
      do {
        const res = await atlas.chatListConversations(activeStack, {
          limit: 100,
          cursor: cursor || undefined,
        })
        const rows = Array.isArray(res?.items) ? res.items : []
        for (const row of rows) {
          const unread = Number(row?.unreadCount || 0)
          if (!Number.isFinite(unread) || unread <= 0) continue
          const selectionId = String(row?.selectionId || '').trim()
          const patientId = String(row?.patientId || '').trim()
          const participantRole = String(row?.participantRole || '').trim().toLowerCase()
          const peerId = String(row?.peerId || '').trim()
          bump(selectionId ? `selection:${selectionId}` : '', unread)
          bump(patientId ? `patient:${patientId}` : '', unread)
          if (participantRole === 'specialist') {
            bump(peerId ? `patient:${peerId}` : '', unread)
          }
        }
        cursor = String(res?.nextCursor || '').trim() || null
        pages += 1
      } while (cursor && pages < 8)
    } catch {
      // Chat metadata may be unavailable briefly during stack warmups.
    }
    return next
  }, [activeStack])

  const unreadForSelection = React.useCallback(
    (selectionId?: string | null) => {
      const selId = String(selectionId || '').trim()
      if (selId) {
        const exact = Number(chatUnreadSignals[`selection:${selId}`] || 0)
        if (Number.isFinite(exact) && exact > 0) return exact
      }
      const item = itemsRef.current.find((entry) => String(entry.selectionId || '').trim() === selId)
      const patientId = String(item?.patientId || '').trim()
      const byPatient = patientId ? Number(chatUnreadSignals[`patient:${patientId}`] || 0) : 0
      return Number.isFinite(byPatient) && byPatient > 0 ? byPatient : 0
    },
    [chatUnreadSignals],
  )

  React.useEffect(() => { load() }, [load])
  React.useEffect(() => {
    let cancelled = false
    const syncUnread = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      const next = await loadUnreadBySelection()
      if (!cancelled) setChatUnreadSignals(next)
    }
    void syncUnread()
    if (typeof window === 'undefined') return () => { cancelled = true }
    const timer = window.setInterval(() => {
      void syncUnread()
    }, 20000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [loadUnreadBySelection])
  React.useEffect(() => { itemsRef.current = items }, [items])
  React.useEffect(() => { notesDraftsRef.current = notesDrafts }, [notesDrafts])
  React.useEffect(() => { loadStripeStatus() }, [loadStripeStatus])
  React.useEffect(() => {
    const stripeParam = searchParams.get('stripe')
    if (stripeParam) loadStripeStatus()
  }, [searchParams, loadStripeStatus])
  React.useEffect(() => {
    const raw = String(searchParams.get('stack') || '').trim().toLowerCase()
    if (!raw) return
    if (stackParamHandled.current === raw) return
    if (!PROVIDER_STACKS.some((stack) => stack.id === raw)) return
    stackParamHandled.current = raw
    if (activeStack !== raw) {
      setActiveStack(raw as ProviderProfession)
    }
  }, [searchParams, activeStack])

  React.useEffect(() => {
    if (!activeStackMeta) return
    loadStackProfile(activeStackMeta.id)
  }, [activeStackMeta, loadStackProfile])

  // Fetch only the active stack once
  // Fetch only the active stack once
  React.useEffect(() => {
    loadStackProfile(activeStack)
  }, [activeStack, loadStackProfile])

  React.useEffect(() => {
    if (previousStackRef.current === activeStack) return
    previousStackRef.current = activeStack
    setItems([])
    setNeedsProviderProfile(false)
    setErr(null)
    setReady(false)
    setNotesOpen(false)
    setNotesActiveId(null)
    setNotesFilterKey(null)
    setNotesDrafts({})
    setNotesSavedAt({})
    setNotesSaving({})
    setNotesError(null)
    setNotesAttachmentUrls({})
    setReviewOpen(false)
    setReviewSelectionId(null)
    setClientCursor(null)
    setClientLoadingMore(false)
    Object.values(notesSaveTimers.current).forEach((timer) => window.clearTimeout(timer))
  }, [activeStack])

  React.useEffect(() => {
    if (availableStacks.length && !availableStacks.some((s) => s.id === activeStack)) {
      setActiveStack(availableStacks[0].id)
    } else if (!availableStacks.length && activeStack !== 'doctor') {
      setActiveStack('doctor')
    }
    // When nothing approved, mark ready to avoid infinite spinner
    if (!availableStacks.length && Object.keys(stackStatuses).length > 0) {
      setNeedsProviderProfile(true)
      setReady(true)
      setLoading(false)
    }
  }, [availableStacks, activeStack, stackStatuses])

  const displayTimeZone = React.useMemo(() => detectTimeZone(), [])
  const fmtWhenLocal = (slotISO?: string, createdAt?: string, selectionCreatedAt?: string | null) => {
    const format = (iso?: string) => {
      if (!iso) return '—'
      try {
        return new Date(iso).toLocaleString('en-AU', {
          timeZone: displayTimeZone,
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })
      } catch {
        return new Date(iso).toLocaleString()
      }
    }
    if (slotISO === CONTACT_SLOT) {
      const submittedAt = createdAt || selectionCreatedAt
      return `Request submitted: ${format(submittedAt)} (${displayTimeZone})`
    }
    return `Requested time: ${format(slotISO)} (${displayTimeZone})`
  }
  const fmtNotesTime = (item: IncomingItem) => {
    const iso = item.slotISO === CONTACT_SLOT ? (item.createdAt || item.selectionCreatedAt) : item.slotISO
    if (!iso) return '—'
    try {
      return new Date(iso).toLocaleString('en-AU', {
        timeZone: displayTimeZone,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    } catch {
      return new Date(iso).toLocaleString()
    }
  }
  const fmtPublishedAt = (iso?: string | null) => {
    if (!iso) return ''
    try {
      return new Date(iso).toLocaleString('en-AU', {
        timeZone: displayTimeZone,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      })
    } catch {
      return new Date(iso).toLocaleString()
    }
  }
  const fmtReviewAt = (iso?: string | null) => {
    if (!iso) return '—'
    try {
      return new Date(iso).toLocaleString('en-AU', {
        timeZone: displayTimeZone,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    } catch {
      return new Date(iso).toLocaleString()
    }
  }
  const sanitizeNoteHtml = React.useCallback((html: string) => {
    return DOMPurify.sanitize(html || '', {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 's', 'blockquote',
        'ul', 'ol', 'li', 'h2', 'h3', 'a', 'img', 'hr',
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'data-key', 'width', 'height'],
    })
  }, [])
  const decodeHtmlEntities = React.useCallback((value: string) => {
    return value
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
  }, [])
  const normalizeNoteText = React.useCallback((html: string) => {
    const sanitized = sanitizeNoteHtml(html || '')
    if (!sanitized) return ''
    return decodeHtmlEntities(sanitized)
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  }, [sanitizeNoteHtml, decodeHtmlEntities])
  const noteHtmlToText = React.useCallback((html: string) => {
    const sanitized = sanitizeNoteHtml(html || '')
    if (!sanitized) return ''
    const hasMarkup = /<\/?[a-z][\s\S]*>/i.test(sanitized)
    const baseline = hasMarkup ? sanitized : sanitized.replace(/\n/g, '<br />')
    let text = baseline
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|h1|h2|h3|h4|h5|h6|li|blockquote)>/gi, '\n')
      .replace(/<li>/gi, '- ')
      .replace(/<[^>]+>/g, ' ')
    text = decodeHtmlEntities(text)
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\s+\n/g, '\n')
      .trim()
    return text
  }, [sanitizeNoteHtml, decodeHtmlEntities])
  const normalizeAttachments = React.useCallback(
    (item?: IncomingItem | null) =>
      Array.isArray(item?.notesAttachments) ? item!.notesAttachments!.filter(Boolean) : [],
    []
  )
  const isImageAttachment = (att?: NoteAttachment | null) => {
    if (!att) return false
    if (att.kind === 'image') return true
    const type = String(att.type || '')
    return type.startsWith('image/')
  }
  const mergeAttachments = React.useCallback((current: NoteAttachment[], next: NoteAttachment[]) => {
    const map = new Map<string, NoteAttachment>()
    current.forEach((att) => {
      if (att?.key) map.set(att.key, att)
    })
    next.forEach((att) => {
      if (att?.key) map.set(att.key, att)
    })
    return Array.from(map.values())
  }, [])
  const getAttachmentsForSelection = React.useCallback((selectionId: string) => {
    const item = itemsRef.current.find((entry) => entry.selectionId === selectionId)
    return normalizeAttachments(item)
  }, [normalizeAttachments])
  const isAllowedNotesFile = (file: File, kind: 'image' | 'file') => {
    const type = String(file.type || '').toLowerCase()
    if (kind === 'image') {
      return NOTE_IMAGE_TYPES.has(type) || type.startsWith('image/')
    }
    if (NOTE_FILE_TYPES.has(type)) return true
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    return NOTE_FILE_EXTS.has(ext)
  }
  const renderNoteHtml = React.useCallback((html: string) => {
    const sanitized = sanitizeNoteHtml(html || '')
    if (!sanitized) return ''
    const hasMarkup = /<\/?[a-z][\s\S]*>/i.test(sanitized)
    const baseline = hasMarkup ? sanitized : sanitized.replace(/\n/g, '<br />')
    if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return baseline
    try {
      const doc = new window.DOMParser().parseFromString(baseline, 'text/html')
      doc.querySelectorAll('img[data-key]').forEach((img) => {
        const key = img.getAttribute('data-key')
        if (!key) return
        const url = notesAttachmentUrls[key]
        if (url) img.setAttribute('src', url)
      })
      return doc.body.innerHTML
    } catch {
      return baseline
    }
  }, [sanitizeNoteHtml, notesAttachmentUrls])
  const getSortTime = React.useCallback((item: IncomingItem) => {
    const created = Date.parse(item.createdAt || item.selectionCreatedAt || '')
    if (Number.isFinite(created)) return created
    const slot = Date.parse(item.slotISO || '')
    return Number.isFinite(slot) ? slot : 0
  }, [])
  const getRequestOrSlotTime = React.useCallback((item: IncomingItem) => {
    if (item.slotISO === CONTACT_SLOT) {
      const submitted = item.createdAt || item.selectionCreatedAt || ''
      return Date.parse(submitted) || 0
    }
    const slot = Date.parse(item.slotISO || '')
    if (Number.isFinite(slot)) return slot
    const created = Date.parse(item.createdAt || item.selectionCreatedAt || '')
    return Number.isFinite(created) ? created : 0
  }, [])
  const notesItems = React.useMemo(() => {
    return items
      .filter((item) => isNotesEligible(item))
      .slice()
      .sort((a, b) => getSortTime(b) - getSortTime(a))
  }, [items, getSortTime])
  const notesItemsFiltered = React.useMemo(() => {
    if (!notesFilterKey) return notesItems
    return notesItems.filter((item) => (getClientKey(item) || item.selectionId) === notesFilterKey)
  }, [notesItems, notesFilterKey])
  const notesTimeLabels = React.useMemo(() => {
    const map: Record<string, string> = {}
    for (const item of notesItemsFiltered) {
      if (!item.selectionId) continue
      map[item.selectionId] = fmtNotesTime(item)
    }
    return map
  }, [notesItemsFiltered])
  const notesClientName = React.useMemo(() => {
    for (const item of notesItemsFiltered) {
      const name = (item.patientName || item.patientContact?.name || '').trim()
      if (name) return name
    }
    return null
  }, [notesItemsFiltered])
  const saveNotes = React.useCallback(
    async (
      selectionId: string,
      notesKey: string,
      value: string,
      options?: { published?: boolean; attachments?: NoteAttachment[] },
    ) => {
      setNotesSaving((prev) => ({ ...prev, [notesKey]: true }))
      try {
        const res = await atlas.saveSelectionNotes(
          activeStack,
          selectionId,
          value,
          options?.published,
          options?.attachments,
        )
        const updatedAt = res?.updatedAt || new Date().toISOString()
        const hasPublishedAt = res && Object.prototype.hasOwnProperty.call(res, 'publishedAt')
        const publishedAt = hasPublishedAt ? res.publishedAt : undefined
        setNotesSavedAt((prev) => ({ ...prev, [notesKey]: updatedAt }))
        let notesChanged = false
        const normalizedValue = normalizeNoteText(value)
        setItems((prev) => prev.map((item) => {
          if (item.selectionId !== notesKey) return item
          const prevNormalized = normalizeNoteText(item.notes || '')
          notesChanged = normalizedValue !== prevNormalized
          const next = { ...item, notes: value || null, notesUpdatedAt: updatedAt }
          if (hasPublishedAt) {
            next.notesPublishedAt = publishedAt
          }
          if (options?.attachments) {
            next.notesAttachments = options.attachments
          }
          if (notesChanged) {
            next.notesSummary = null
            next.notesSummaryUpdatedAt = null
          }
          return next
        }))
        if (notesChanged) {
          setNotesSummaryDrafts((prev) => ({ ...prev, [notesKey]: '' }))
          setNotesSummarySavedAt((prev) => ({ ...prev, [notesKey]: null }))
          setNotesSummaryEditing((prev) => ({ ...prev, [notesKey]: false }))
        }
        if (options?.published === true) {
          setNotesDraftPreview((prev) => ({ ...prev, [notesKey]: '' }))
          setNotesDraftErrors((prev) => ({ ...prev, [notesKey]: null }))
        }
        setNotesError(null)
      } catch (e: any) {
        setNotesError(e?.message || 'Failed to save notes.')
      } finally {
        setNotesSaving((prev) => ({ ...prev, [notesKey]: false }))
      }
    },
    [activeStack, normalizeNoteText]
  )
  const getSavedSummaryForSelection = React.useCallback((selectionId: string) => {
    const item = itemsRef.current.find((entry) => entry.selectionId === selectionId)
    return item?.notesSummary || ''
  }, [])
  const generateNotesSummary = React.useCallback(async (selectionId: string) => {
    if (!selectionId) return
    setNotesSummaryGenerating((prev) => ({ ...prev, [selectionId]: true }))
    setNotesSummaryErrors((prev) => ({ ...prev, [selectionId]: null }))
    try {
      const res = await atlas.generateSelectionNotesSummary(activeStack, selectionId)
      const summary = res?.summary || ''
      const updatedAt = res?.updatedAt || new Date().toISOString()
      setNotesSummaryDrafts((prev) => ({ ...prev, [selectionId]: summary }))
      setNotesSummarySavedAt((prev) => ({ ...prev, [selectionId]: updatedAt }))
      setNotesSummaryEditing((prev) => ({ ...prev, [selectionId]: false }))
      setItems((prev) => prev.map((item) => (
        item.selectionId === selectionId
          ? { ...item, notesSummary: summary || null, notesSummaryUpdatedAt: updatedAt }
          : item
      )))
    } catch (err: any) {
      setNotesSummaryErrors((prev) => ({ ...prev, [selectionId]: err?.message || 'Failed to generate summary.' }))
    } finally {
      setNotesSummaryGenerating((prev) => ({ ...prev, [selectionId]: false }))
    }
  }, [activeStack])
  const saveNotesSummary = React.useCallback(async (selectionId: string, value: string) => {
    if (!selectionId) return
    setNotesSummarySaving((prev) => ({ ...prev, [selectionId]: true }))
    setNotesSummaryErrors((prev) => ({ ...prev, [selectionId]: null }))
    try {
      const res = await atlas.saveSelectionNotesSummary(activeStack, selectionId, value)
      const summary = res?.summary || ''
      const updatedAt = res?.updatedAt || null
      setNotesSummaryDrafts((prev) => ({ ...prev, [selectionId]: summary }))
      setNotesSummarySavedAt((prev) => ({ ...prev, [selectionId]: updatedAt }))
      setNotesSummaryEditing((prev) => ({ ...prev, [selectionId]: false }))
      setItems((prev) => prev.map((item) => (
        item.selectionId === selectionId
          ? { ...item, notesSummary: summary || null, notesSummaryUpdatedAt: updatedAt }
          : item
      )))
    } catch (err: any) {
      setNotesSummaryErrors((prev) => ({ ...prev, [selectionId]: err?.message || 'Failed to save summary.' }))
    } finally {
      setNotesSummarySaving((prev) => ({ ...prev, [selectionId]: false }))
    }
  }, [activeStack])
  const cancelNotesSummaryEdit = React.useCallback((selectionId: string) => {
    if (!selectionId) return
    setNotesSummaryDrafts((prev) => ({ ...prev, [selectionId]: getSavedSummaryForSelection(selectionId) }))
    setNotesSummaryEditing((prev) => ({ ...prev, [selectionId]: false }))
  }, [getSavedSummaryForSelection])
  const ensureAttachmentUrls = React.useCallback(
    async (attachments: NoteAttachment[]) => {
      const missing = attachments
        .map((att) => att?.key)
        .filter((key): key is string => Boolean(key))
        .filter((key) => !notesAttachmentUrls[key])
      if (!missing.length) return
      try {
        const signed = await Promise.all(
          missing.map(async (key) => {
            try {
              const res = await atlas.signUploadKey(activeStack, key)
              return res?.url ? { key, url: res.url } : null
            } catch {
              return null
            }
          })
        )
        setNotesAttachmentUrls((prev) => {
          const next = { ...prev }
          signed.forEach((entry) => {
            if (entry?.url) next[entry.key] = entry.url
          })
          return next
        })
      } catch {
        // ignore signing failures
      }
    },
    [activeStack, notesAttachmentUrls]
  )
  const queueSaveNotes = React.useCallback(
    (selectionId: string, notesKey: string, value: string) => {
      if (notesSaveTimers.current[notesKey]) {
        window.clearTimeout(notesSaveTimers.current[notesKey])
      }
      notesSaveTimers.current[notesKey] = window.setTimeout(() => {
        const attachments = getAttachmentsForSelection(notesKey)
        saveNotes(selectionId, notesKey, value, attachments.length ? { attachments } : undefined)
      }, 600)
    },
    [saveNotes, getAttachmentsForSelection]
  )
  const updateNotesDraft = (selectionId: string, notesKey: string, value: string) => {
    setNotesDrafts((prev) => ({ ...prev, [notesKey]: value }))
    setNotesError(null)
    setNotesDraftErrors((prev) => ({ ...prev, [selectionId]: null }))
    queueSaveNotes(selectionId, notesKey, value)
  }
  const updateNotesSummaryDraft = React.useCallback((selectionId: string, value: string) => {
    setNotesSummaryDrafts((prev) => ({ ...prev, [selectionId]: value }))
    setNotesSummaryErrors((prev) => ({ ...prev, [selectionId]: null }))
  }, [])
  const getDraftSourceText = React.useCallback((selectionId: string) => {
    const currentHtml = notesDraftsRef.current[selectionId]
    const item = itemsRef.current.find((entry) => entry.selectionId === selectionId)
    const savedHtml = item?.notes || ''
    const rawHtml = typeof currentHtml === 'string' ? currentHtml : savedHtml
    return noteHtmlToText(rawHtml || '')
  }, [noteHtmlToText])
  const generateNotesDraft = React.useCallback(async (selectionId: string) => {
    if (!selectionId) return
    const sourceText = getDraftSourceText(selectionId)
    if (!sourceText) {
      setNotesDraftErrors((prev) => ({ ...prev, [selectionId]: 'Add notes in the editor before generating a draft.' }))
      return
    }
    setNotesDraftGenerating((prev) => ({ ...prev, [selectionId]: true }))
    setNotesDraftErrors((prev) => ({ ...prev, [selectionId]: null }))
    try {
      const res = await atlas.generateSelectionNotesDraft(activeStack, selectionId, sourceText)
      const draftHtml = res?.draftHtml || ''
      if (!draftHtml) throw new Error('Draft not available.')
      setNotesDraftPreview((prev) => ({ ...prev, [selectionId]: draftHtml }))
    } catch (err: any) {
      setNotesDraftErrors((prev) => ({ ...prev, [selectionId]: err?.message || 'Failed to generate draft.' }))
    } finally {
      setNotesDraftGenerating((prev) => ({ ...prev, [selectionId]: false }))
    }
  }, [activeStack, getDraftSourceText])
  const discardNotesDraft = React.useCallback((selectionId: string) => {
    setNotesDraftPreview((prev) => ({ ...prev, [selectionId]: '' }))
    setNotesDraftErrors((prev) => ({ ...prev, [selectionId]: null }))
  }, [])
  const applyNotesDraft = React.useCallback((selectionId: string, mode: 'replace' | 'add') => {
    const draftHtml = notesDraftPreview[selectionId]
    if (!draftHtml) return
    const notesKey = selectionId
    const current = notesDrafts[notesKey] ?? ''
    const separator = current.trim() ? '<p><br /></p>' : ''
    const next = mode === 'replace' ? draftHtml : `${current}${separator}${draftHtml}`
    updateNotesDraft(selectionId, notesKey, next)
    discardNotesDraft(selectionId)
  }, [notesDraftPreview, notesDrafts, updateNotesDraft, discardNotesDraft])
  const refreshNotes = React.useCallback(async () => {
    setNotesError(null)
    try {
      const res = await atlas.getIncomingSelections(activeStack, { includeAll: true })
      const list = (res?.items ?? []) as IncomingItem[]
      setItems((prev) => {
        const map = new Map<string, IncomingItem>()
        for (const item of prev) {
          if (item.selectionId) map.set(item.selectionId, item)
        }
        for (const item of list) {
          if (item.selectionId) map.set(item.selectionId, item)
        }
        return Array.from(map.values())
      })
      applyNotesFromItems(list, { overwrite: true })
    } catch (e: any) {
      setNotesError(e?.message || 'Failed to refresh notes.')
    }
  }, [activeStack, applyNotesFromItems])
  const openNotes = (selectionId?: string) => {
    setViewOpen(false)
    setViewSelectionId(null)
    setPaymentOpen(false)
    setPaymentSelectionId(null)
    setMeetingOpen(false)
    setMeetingSelectionId(null)
    setReviewOpen(false)
    setReviewSelectionId(null)
    setNotesOpen(true)
    const picked = selectionId ? items.find((item) => item.selectionId === selectionId) : null
    const key = picked ? (getClientKey(picked) || picked.selectionId) : (selectionId || null)
    setNotesFilterKey(key)
    setNotesActiveId(selectionId || null)
    openChatForSelection(selectionId)
    refreshNotes()
  }
  const closeNotes = () => {
    setNotesOpen(false)
    setNotesError(null)
    setNotesFilterKey(null)
    setNotesSummaryErrors({})
    setNotesSummaryEditing({})
  }
  const openChatForSelection = React.useCallback(
    (selectionId?: string | null) => {
      const id = String(selectionId || '').trim()
      if (!id) return
      const item = itemsRef.current.find((entry) => entry.selectionId === id)
      const patientId = String(item?.patientId || '').trim()
      setChatUnreadSignals((prev) => {
        const next = { ...prev }
        delete next[`selection:${id}`]
        if (patientId) delete next[`patient:${patientId}`]
        return next
      })
      const clientName =
        (item?.patientName || item?.patientContact?.name || '').trim() || 'Client'
      window.dispatchEvent(
        new CustomEvent('chat:open', {
          detail: {
            profession: activeStack,
            selectionId: id,
            patientId: item?.patientId,
            specialistId: item?.specialistId,
            peerName: clientName,
            patientName: clientName,
            minimized: false,
          },
        }),
      )
    },
    [activeStack],
  )
  const openView = (selectionId: string) => {
    setPaymentOpen(false)
    setPaymentSelectionId(null)
    setNotesOpen(false)
    setNotesFilterKey(null)
    setNotesActiveId(null)
    setMeetingOpen(false)
    setMeetingSelectionId(null)
    setReviewOpen(false)
    setReviewSelectionId(null)
    setViewSelectionId(selectionId)
    setViewOpen(true)
    openChatForSelection(selectionId)
  }
  const closeView = () => {
    setViewOpen(false)
    setViewSelectionId(null)
  }
  const openPayment = (selectionId: string) => {
    setViewOpen(false)
    setViewSelectionId(null)
    setNotesOpen(false)
    setNotesFilterKey(null)
    setNotesActiveId(null)
    setMeetingOpen(false)
    setMeetingSelectionId(null)
    setReviewOpen(false)
    setReviewSelectionId(null)
    setPaymentSelectionId(selectionId)
    setPaymentOpen(true)
    openChatForSelection(selectionId)
  }
  const closePayment = () => {
    setPaymentOpen(false)
    setPaymentSelectionId(null)
  }
  const openMeeting = (selectionId: string) => {
    setViewOpen(false)
    setViewSelectionId(null)
    setNotesOpen(false)
    setNotesFilterKey(null)
    setNotesActiveId(null)
    setPaymentOpen(false)
    setPaymentSelectionId(null)
    setReviewOpen(false)
    setReviewSelectionId(null)
    setMeetingSelectionId(selectionId)
    setMeetingOpen(true)
    openChatForSelection(selectionId)
  }
  const closeMeeting = () => {
    setMeetingOpen(false)
    setMeetingSelectionId(null)
  }
  const openReview = (selectionId: string) => {
    setViewOpen(false)
    setViewSelectionId(null)
    setNotesOpen(false)
    setNotesFilterKey(null)
    setNotesActiveId(null)
    setPaymentOpen(false)
    setPaymentSelectionId(null)
    setMeetingOpen(false)
    setMeetingSelectionId(null)
    setReviewReminderOpen(false)
    setReviewReminderSelectionId(null)
    setReviewReplyError(null)
    setReviewReplyNotice(null)
    setReviewSelectionId(selectionId)
    setReviewOpen(true)
  }
  const closeReview = () => {
    setReviewOpen(false)
    setReviewSelectionId(null)
    setReviewReplyError(null)
    setReviewReplyNotice(null)
  }
  const openReviewReminder = (selectionId: string) => {
    setViewOpen(false)
    setViewSelectionId(null)
    setNotesOpen(false)
    setNotesFilterKey(null)
    setNotesActiveId(null)
    setPaymentOpen(false)
    setPaymentSelectionId(null)
    setMeetingOpen(false)
    setMeetingSelectionId(null)
    setReviewOpen(false)
    setReviewSelectionId(null)
    setReviewReminderSelectionId(selectionId)
    setReviewReminderMessage(reviewReminderDefaultMessage)
    setReviewReminderSaveDefault(false)
    setReviewReminderError(null)
    setReviewReminderSuccess(null)
    setReviewReminderDirty(false)
    setReviewReminderOpen(true)
  }
  const closeReviewReminder = () => {
    setReviewReminderOpen(false)
    setReviewReminderSelectionId(null)
    setReviewReminderError(null)
    setReviewReminderSuccess(null)
  }
  const openNotesFromView = (selectionId: string) => {
    closeView()
    openNotes(selectionId)
  }
  const handleStripeOnboard = React.useCallback(async () => {
    try {
      const res = await api.stripeOnboard(`/dashboard?stack=${activeStack}`)
      if (res?.url) window.location.href = res.url
    } catch (err: any) {
      setStripeStatusError(err?.message || 'Failed to start Stripe onboarding')
    }
  }, [activeStack])

  const sendReviewReminder = React.useCallback(async () => {
    if (!reviewReminderSelectionId) return
    setReviewReminderSending(true)
    setReviewReminderError(null)
    setReviewReminderSuccess(null)
    try {
      await atlas.sendReviewReminder(activeStack, reviewReminderSelectionId, {
        message: reviewReminderMessage.trim(),
        saveDefault: reviewReminderSaveDefault,
      })
      if (reviewReminderSaveDefault) {
        setStackProfiles((prev) => {
          const current = prev[activeStack] || {}
          const currentProfile =
            current.profile && typeof current.profile === 'object' ? current.profile : null
          return {
            ...prev,
            [activeStack]: {
              ...current,
              reviewReminderMessage: reviewReminderMessage.trim(),
              ...(currentProfile
                ? { profile: { ...currentProfile, reviewReminderMessage: reviewReminderMessage.trim() } }
                : null),
            },
          }
        })
      }
      setReviewReminderSuccess('Reminder sent to the client.')
    } catch (err: any) {
      setReviewReminderError(err?.message || 'Failed to send reminder')
    } finally {
      setReviewReminderSending(false)
    }
  }, [
    activeStack,
    reviewReminderSelectionId,
    reviewReminderMessage,
    reviewReminderSaveDefault,
  ])

  const overlayOpen = notesOpen || viewOpen || paymentOpen || meetingOpen || reviewOpen || reviewReminderOpen

  React.useEffect(() => {
    overlayOpenRef.current = overlayOpen
  }, [overlayOpen])

  React.useEffect(() => {
    const handlePopState = () => {
      if (ignorePopRef.current) {
        ignorePopRef.current = false
        return
      }
      if (overlayOpenRef.current) {
        closeByPopRef.current = true
        closeNotes()
        closeView()
        closePayment()
        closeMeeting()
        closeReview()
        closeReviewReminder()
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  React.useEffect(() => {
    if (overlayOpen && !overlayStateRef.current) {
      window.history.pushState({ inboxOverlay: true }, '', window.location.href)
      overlayStateRef.current = true
      return
    }
    if (!overlayOpen && overlayStateRef.current) {
      if (closeByPopRef.current) {
        closeByPopRef.current = false
        overlayStateRef.current = false
        return
      }
      ignorePopRef.current = true
      window.history.back()
      overlayStateRef.current = false
    }
  }, [overlayOpen])
  const uploadNotesAttachment = React.useCallback(
    async (selectionId: string, file: File, kind: 'image' | 'file') => {
      if (!file) return null
      if (!isAllowedNotesFile(file, kind)) {
        setNotesError('Upload images, PDFs, Word, Excel, CSV, or TXT files.')
        return null
      }
      if (file.size > MAX_NOTES_FILE_BYTES) {
        setNotesError(`File must be ${MAX_NOTES_FILE_MB} MB or smaller.`)
        return null
      }
      setNotesError(null)
      try {
        const hint = file.name ? file.name.replace(/[^a-z0-9]/gi, '') : 'note'
        const contentType = file.type || (kind === 'image' ? 'image/jpeg' : 'application/octet-stream')
        const presign = await atlas.presignUpload(activeStack, hint, contentType)
        await uploadToS3(presign, file)
        const key = presign?.key || presign?.fields?.key
        if (!key) throw new Error('Upload failed - missing key')
        let signedUrl = ''
        try {
          const signed = await atlas.signUploadKey(activeStack, key)
          signedUrl = signed?.url || ''
        } catch {
          // ignore
        }
        const attachment: NoteAttachment = {
          key,
          name: file.name || null,
          type: file.type || null,
          size: file.size,
          kind,
          uploadedAt: new Date().toISOString(),
        }
        const existing = getAttachmentsForSelection(selectionId)
        const next = mergeAttachments(existing, [attachment])
        setItems((prev) => prev.map((item) => (
          item.selectionId === selectionId ? { ...item, notesAttachments: next } : item
        )))
        if (signedUrl) {
          setNotesAttachmentUrls((prev) => ({ ...prev, [key]: signedUrl }))
        }
        const value = notesDraftsRef.current[selectionId] ?? ''
        await saveNotes(selectionId, selectionId, value, { attachments: next })
        return { attachment, url: signedUrl }
      } catch (err: any) {
        setNotesError(err?.message || 'Upload failed.')
        return null
      }
    },
    [activeStack, getAttachmentsForSelection, mergeAttachments, saveNotes, isAllowedNotesFile]
  )
  React.useEffect(() => {
    if (!notesOpen) return
    applyNotesFromItems(notesItems)
  }, [notesOpen, notesItems, applyNotesFromItems])
  React.useEffect(() => {
    if (!notesOpen) return
    if (!notesItemsFiltered.length) {
      setNotesActiveId(null)
      return
    }
    if (!notesActiveId || !notesItemsFiltered.some(item => item.selectionId === notesActiveId)) {
      setNotesActiveId(notesItemsFiltered[0].selectionId)
    }
  }, [notesOpen, notesItemsFiltered, notesActiveId])
  React.useEffect(() => {
    if (!notesOpen) return
    const attachments = notesItemsFiltered.flatMap((item) => normalizeAttachments(item))
    if (!attachments.length) return
    ensureAttachmentUrls(attachments)
  }, [notesOpen, notesItemsFiltered, normalizeAttachments, ensureAttachmentUrls])
  React.useEffect(() => {
    return () => {
      Object.values(notesSaveTimers.current).forEach((timer) => window.clearTimeout(timer))
    }
  }, [])
  React.useEffect(() => {
    const target = searchParams.get('notes')
    if (!target) return
    if (!ready || loading) return
    if (notesParamHandled.current === target) return
    notesParamHandled.current = target
    openNotes(target)
  }, [searchParams, ready, loading])
  React.useEffect(() => {
    const target = searchParams.get('meeting')
    if (!target) return
    if (!ready || loading) return
    if (meetingParamHandled.current === target) return
    meetingParamHandled.current = target
    openMeeting(target)
  }, [searchParams, ready, loading])
  React.useEffect(() => {
    const target = String(searchParams.get('review') || '').trim()
    if (!target) return
    if (!ready || loading) return
    const key = `${activeStack}:${target}`
    if (reviewParamHandled.current === key) return
    if (!items.some((item) => item.selectionId === target)) return
    reviewParamHandled.current = key
    openReview(target)
  }, [searchParams, ready, loading, activeStack, items])
  React.useEffect(() => {
    const handleRefresh = () => {
      closeNotes()
      closeView()
      closePayment()
      closeMeeting()
      load()
    }
    window.addEventListener('inbox:refresh', handleRefresh)
    return () => window.removeEventListener('inbox:refresh', handleRefresh)
  }, [load])
  const handleStackSwitch = (stackId: ProviderProfession) => {
    if (stackId === activeStack) return
    setActiveStack(stackId)
  }
  const renderStackSwitcher = () => {
    if (availableStacks.length <= 1) return null
    return (
      <div className="chip-row" role="tablist" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
        {availableStacks.map(stack => (
          <button
            key={stack.id}
            type="button"
            className={`chip ${stack.id === activeStack ? 'active' : ''}`}
            onClick={() => handleStackSwitch(stack.id)}
          >
            {stack.label}
          </button>
        ))}
      </div>
    )
  }

  const categorizeItem = (item: IncomingItem) => {
    const status = String(item.status || '').toUpperCase()
    if (status === 'ACCEPTED' || status === 'CONFIRMED') return 'confirmed'
    if (status === 'PENDING') return slotExpired(item.slotISO) ? 'expired' : 'pending'
    return 'expired'
  }
  const getInvoiceBadge = (item: IncomingItem) => {
    const status = String(item.invoiceStatus || '').toLowerCase()
    if (status === 'paid') return { status: 'paid' as const, className: 'paid', title: 'Invoice paid' }
    if (status === 'open' || status === 'sent') {
      return { status: 'sent' as const, className: 'sent', title: 'Invoice sent' }
    }
    return null
  }
  const renderInvoiceIcon = (status: 'paid' | 'sent') => {
    const baseIcon = status === 'paid'
      ? <CheckCircle2 size={16} strokeWidth={2} aria-hidden="true" />
      : <FileText size={16} strokeWidth={2} aria-hidden="true" />
    return (
      <span className="invoice-icon-stack" aria-hidden="true">
        {baseIcon}
        <span className="invoice-icon-overlay" aria-hidden="true">
          <DollarSign size={10} strokeWidth={2.2} />
        </span>
      </span>
    )
  }
  const getReviewBadge = (item: IncomingItem) => {
    const submitted = Boolean(item.reviewReceived?.submitted)
    return {
      submitted,
      className: submitted ? 'review-received' : 'review-pending',
      ariaLabel: submitted ? 'Client review received' : 'Client review pending',
    }
  }
  const filteredItems = React.useMemo(() => {
    if (statusFilter === 'clients') return []
    const rows = items.filter((item) => {
      const category = categorizeItem(item)
      if (statusFilter === 'confirmed') {
        return category === 'confirmed' || category === 'pending' || category === 'expired'
      }
      return category === statusFilter
    })
    const sorted = rows.slice()
    if (statusFilter !== 'confirmed') {
      return sorted.sort((a, b) => getSortTime(b) - getSortTime(a))
    }
    const dir = sortDir === 'asc' ? 1 : -1
    const paymentRank = (item: IncomingItem) => {
      const status = String(item.invoiceStatus || '').toLowerCase()
      if (status === 'paid') return 2
      if (status === 'open' || status === 'sent') return 1
      return 0
    }
    return sorted.sort((a, b) => {
      if (sortKey === 'name') {
        const nameA = (a.patientName || a.patientContact?.name || '').trim().toLowerCase()
        const nameB = (b.patientName || b.patientContact?.name || '').trim().toLowerCase()
        const cmp = nameA.localeCompare(nameB)
        if (cmp !== 0) return cmp * dir
      } else if (sortKey === 'payment') {
        const cmp = (paymentRank(a) - paymentRank(b)) * dir
        if (cmp !== 0) return cmp
      } else {
        const cmp = (getRequestOrSlotTime(a) - getRequestOrSlotTime(b)) * dir
        if (cmp !== 0) return cmp
      }
      return getRequestOrSlotTime(b) - getRequestOrSlotTime(a)
    })
  }, [items, statusFilter, getSortTime, getRequestOrSlotTime, sortKey, sortDir])
  const clientRows = React.useMemo(() => {
    const map = new Map<string, { key: string; name: string; latest: IncomingItem; time: number }>()
    for (const item of items) {
      const key = getClientKey(item) || item.selectionId
      if (!key) continue
      const time = getRequestOrSlotTime(item)
      const existing = map.get(key)
      if (!existing || time > existing.time) {
        const name =
          (item.patientName || item.patientContact?.name || '').trim() ||
          'Client'
        map.set(key, { key, name, latest: item, time })
      }
    }
    const rows = Array.from(map.values())
    const dir = sortDir === 'asc' ? 1 : -1
    const key = sortKey === 'payment' ? 'date' : sortKey
    return rows.sort((a, b) => {
      if (key === 'name') {
        const cmp = a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        if (cmp !== 0) return cmp * dir
      }
      return (a.time - b.time) * dir
    })
  }, [items, getRequestOrSlotTime, sortKey, sortDir])

  const stripeHasToken = stripeStatus?.hasAccessToken !== false
  const stripeNeedsReconnect = stripeStatus?.connected && stripeStatus?.hasAccessToken === false
  const stripeReady =
    stripeStatus?.connected &&
    stripeStatus?.chargesEnabled &&
    stripeStatus?.payoutsEnabled &&
    stripeHasToken &&
    (!stripeStatus?.accountType || stripeStatus?.accountType === 'standard')
  const stripeNeedsSetup = stripeStatus?.connected && !stripeReady && !stripeNeedsReconnect
  const stripeActionLabel = stripeStatusLoading
    ? 'Checking Stripe…'
    : stripeReady
      ? 'Payment'
      : stripeNeedsReconnect
        ? 'Reconnect payments'
      : stripeNeedsSetup
        ? 'Finish setup'
        : 'Set up payments'
  const clientSortKey = sortKey === 'payment' ? 'date' : sortKey
  const sortIcon = (key: 'date' | 'name' | 'payment') => {
    if (sortKey !== key) return <ArrowUpDown size={14} />
    return sortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
  }
  const clientSortIcon = (key: 'date' | 'name') => {
    if (clientSortKey !== key) return <ArrowUpDown size={14} />
    return sortDir === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
  }
  const handleSort = (key: 'date' | 'name' | 'payment') => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'))
        return prev
      }
      setSortDir(key === 'date' ? 'desc' : 'asc')
      return key
    })
  }
  const handlePaymentAction = React.useCallback(
    (selectionId: string) => {
      if (stripeReady) {
        openPayment(selectionId)
      } else {
        handleStripeOnboard()
      }
    },
    [stripeReady, openPayment, handleStripeOnboard],
  )
  const loadMoreClients = React.useCallback(async () => {
    if (!clientCursor || clientLoadingMore) return
    setClientLoadingMore(true)
    try {
      const res = await atlas.getIncomingSelections(activeStack, { includeAll: true, cursor: clientCursor })
      const list = (res?.items ?? []) as IncomingItem[]
      setItems((prev) => {
        const map = new Map<string, IncomingItem>()
        for (const item of prev) {
          if (item.selectionId) map.set(item.selectionId, item)
        }
        for (const item of list) {
          if (item.selectionId) map.set(item.selectionId, item)
        }
        return Array.from(map.values())
      })
      applyNotesFromItems(list)
      setClientCursor(res?.nextCursor || null)
    } catch (e: any) {
      setErr(e?.message || 'Failed to load more clients.')
    } finally {
      setClientLoadingMore(false)
    }
  }, [activeStack, clientCursor, clientLoadingMore, applyNotesFromItems])

  const renderRequests = () => (
      <section className="card section-card">
        <div className="chip-row inbox-status-tabs" role="tablist" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
          {[
            { id: 'confirmed', label: 'Bookings' },
            { id: 'clients', label: 'Clients' },
          ].map(filter => (
            <button
              key={filter.id}
              type="button"
              className={`chip ${statusFilter === filter.id ? 'active' : ''}`}
              onClick={() => setStatusFilter(filter.id as typeof statusFilter)}
            >
              {filter.label}
            </button>
          ))}
        </div>
        {statusFilter === 'confirmed' && (
          <div className="inbox-sort-row" aria-label="Sort bookings">
            <span className="hint">Sort by</span>
            <button
              type="button"
              className={`btn btn-ghost btn-compact${sortKey === 'name' ? ' is-active' : ''}`}
              onClick={() => handleSort('name')}
            >
              {sortIcon('name')}
              Name
            </button>
            <button
              type="button"
              className={`btn btn-ghost btn-compact${sortKey === 'date' ? ' is-active' : ''}`}
              onClick={() => handleSort('date')}
            >
              {sortIcon('date')}
              Date
            </button>
            <button
              type="button"
              className={`btn btn-ghost btn-compact${sortKey === 'payment' ? ' is-active' : ''}`}
              onClick={() => handleSort('payment')}
            >
              {sortIcon('payment')}
              Payment
            </button>
          </div>
        )}
        {statusFilter === 'clients' && (
          <div className="inbox-sort-row" aria-label="Sort clients">
            <span className="hint">Sort by</span>
            <button
              type="button"
              className={`btn btn-ghost btn-compact${clientSortKey === 'name' ? ' is-active' : ''}`}
              onClick={() => handleSort('name')}
            >
              {clientSortIcon('name')}
              Name
            </button>
            <button
              type="button"
              className={`btn btn-ghost btn-compact${clientSortKey === 'date' ? ' is-active' : ''}`}
              onClick={() => handleSort('date')}
            >
              {clientSortIcon('date')}
              Date
            </button>
          </div>
        )}

        {err && <div className="banner error">{err}</div>}
        {loading && <Spinner label="Loading requests…" />}
        {!loading && !err && statusFilter !== 'clients' && filteredItems.length === 0 && (
          <div style={{ textAlign:'center', padding:'1.25rem 0' }}>
            <p>No {statusFilter === 'confirmed' ? 'bookings' : statusFilter}.</p>
            <p className="hint">Try another filter or check back soon.</p>
          </div>
        )}
        {!loading && !err && statusFilter === 'clients' && clientRows.length === 0 && (
          <div style={{ textAlign:'center', padding:'1.25rem 0' }}>
            <p>No clients yet.</p>
            <p className="hint">Once you receive a request, clients will appear here.</p>
          </div>
        )}

        {!!filteredItems.length && statusFilter !== 'clients' && (
          <ul className="list">
            {filteredItems.map(i => {
              const category = categorizeItem(i)
              const expired = category === 'expired'
              const confirmed = category === 'confirmed'
              const showNotes = isNotesEligible(i)
              const showPayment = category === 'pending' || confirmed
              const showMeeting = category === 'pending' || confirmed
              const unreadCount = unreadForSelection(i.selectionId)
              const icon =
                confirmed ? <CheckCircle2 size={18} strokeWidth={1.8} color="#1B5E20" /> :
                expired ? <AlertTriangle size={18} strokeWidth={1.8} color="#B71C1C" /> :
                <InboxIcon size={18} strokeWidth={1.8} color="#0D47A1" />
              const clientName =
                (i.patientName || i.patientContact?.name || '').trim() || 'Client'
              const invoiceBadge = confirmed ? getInvoiceBadge(i) : null
              const reviewBadge = confirmed ? getReviewBadge(i) : null
              const handleNameClick = () => {
                openView(i.selectionId)
              }
              return (
                <li
                  key={i.selectionId}
                  className="item"
                  role="listitem"
                >
                  <div className="list-row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}>
                    <div className="list-icon">
                      {icon}
                    </div>
                    <div className="list-details" style={{ flex: 1, minWidth: 240 }}>
                      <div className="list-primary inline-gap-2" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="list-name-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleNameClick()
                          }}
                        >
                          {clientName}
                        </button>
                        {invoiceBadge && (
                          <button
                            type="button"
                            className={`invoice-status-icon instant-tooltip ${invoiceBadge.className}`}
                            data-tooltip={invoiceBadge.title}
                            aria-label={invoiceBadge.title}
                            onClick={(e) => {
                              e.stopPropagation()
                              openPayment(i.selectionId)
                            }}
                          >
                            {renderInvoiceIcon(invoiceBadge.status)}
                          </button>
                        )}
                        {reviewBadge && (
                          <button
                            type="button"
                            className={`review-status instant-tooltip ${reviewBadge.className}`}
                            data-tooltip={reviewBadge.ariaLabel}
                            aria-label={reviewBadge.ariaLabel}
                            aria-disabled={!reviewBadge.submitted}
                            onClick={(e) => {
                              e.stopPropagation()
                              if (reviewBadge.submitted) {
                                openReview(i.selectionId)
                              } else {
                                openReviewReminder(i.selectionId)
                              }
                            }}
                          >
                            <span className="review-icon-stack" aria-hidden="true">
                              <Star size={16} strokeWidth={2} />
                              {reviewBadge.submitted ? null : null}
                            </span>
                          </button>
                        )}
                        {!confirmed && (
                          <span className={`status-pill ${expired ? 'status-lost' : 'status-pending'}`}>
                            {expired ? 'Expired' : 'Pending'}
                          </span>
                        )}
                      </div>
                      <div className="list-meta">
                        <span>{fmtWhenLocal(i.slotISO, i.createdAt, i.selectionCreatedAt)}</span>
                      </div>
                    </div>
                    <div className="list-cta inbox-actions-row">
                      <div className="message-action-wrap">
                        {unreadCount > 0 && (
                          <span className="message-action-alert">
                            <span className="message-action-alert-dot" aria-hidden />
                            New message
                          </span>
                        )}
                        <button
                          type="button"
                          className={`btn btn-primary inbox-message-btn message-action-btn${unreadCount > 0 ? ' has-unread' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation()
                            openChatForSelection(i.selectionId)
                          }}
                        >
                          <MessageSquare size={16} strokeWidth={2.1} />
                          Message client
                          {unreadCount > 0 && (
                            <span className="message-action-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                          )}
                        </button>
                      </div>
                      {showNotes && (
                        <button
                          type="button"
                          className="btn btn-notes instant-tooltip"
                          data-tooltip="AI tools available in Notes"
                          onClick={(e) => {
                            e.stopPropagation()
                            openNotes(i.selectionId)
                          }}
                        >
                          NotesAI
                        </button>
                      )}
                      {showPayment && (
                        <button
                          type="button"
                          className="btn btn-payment"
                          onClick={(e) => {
                            e.stopPropagation()
                            handlePaymentAction(i.selectionId)
                          }}
                          disabled={stripeStatusLoading}
                        >
                          {stripeActionLabel}
                        </button>
                      )}
                      {showMeeting && (
                        <button
                          type="button"
                          className="btn btn-meeting"
                          onClick={(e) => {
                            e.stopPropagation()
                            openMeeting(i.selectionId)
                          }}
                        >
                          Meeting
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
        {!!clientRows.length && statusFilter === 'clients' && (
          <ul className="list">
            {clientRows.map((row) => {
              const category = categorizeItem(row.latest)
              const expired = category === 'expired'
              const confirmed = category === 'confirmed'
              const showNotes = isNotesEligible(row.latest)
              const showPayment = category === 'pending' || confirmed
              const showMeeting = category === 'pending' || confirmed
              const unreadCount = unreadForSelection(row.latest.selectionId)
              const icon =
                confirmed ? <CheckCircle2 size={18} strokeWidth={1.8} color="#1B5E20" /> :
                expired ? <AlertTriangle size={18} strokeWidth={1.8} color="#B71C1C" /> :
                <InboxIcon size={18} strokeWidth={1.8} color="#0D47A1" />
              return (
                <li key={row.key} className="item" role="listitem">
                  <div className="list-row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}>
                    <div className="list-icon">
                      {icon}
                    </div>
                    <div className="list-details" style={{ flex: 1, minWidth: 240 }}>
                      <div className="list-primary inline-gap-2" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          className="list-name-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            openView(row.latest.selectionId)
                          }}
                        >
                          {row.name}
                        </button>
                      </div>
                      <div className="list-meta">
                        <span>{fmtWhenLocal(row.latest.slotISO, row.latest.createdAt, row.latest.selectionCreatedAt)}</span>
                      </div>
                    </div>
                    <div className="list-cta inbox-actions-row">
                      <div className="message-action-wrap">
                        {unreadCount > 0 && (
                          <span className="message-action-alert">
                            <span className="message-action-alert-dot" aria-hidden />
                            New message
                          </span>
                        )}
                        <button
                          type="button"
                          className={`btn btn-primary inbox-message-btn message-action-btn${unreadCount > 0 ? ' has-unread' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation()
                            openChatForSelection(row.latest.selectionId)
                          }}
                        >
                          <MessageSquare size={16} strokeWidth={2.1} />
                          Message client
                          {unreadCount > 0 && (
                            <span className="message-action-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                          )}
                        </button>
                      </div>
                      {showNotes && (
                        <button
                          type="button"
                          className="btn btn-notes instant-tooltip"
                          data-tooltip="AI tools available in Notes"
                          onClick={(e) => {
                            e.stopPropagation()
                            openNotes(row.latest.selectionId)
                          }}
                        >
                          NotesAI
                        </button>
                      )}
                      {showPayment && (
                        <button
                          type="button"
                          className="btn btn-payment"
                          onClick={(e) => {
                            e.stopPropagation()
                            handlePaymentAction(row.latest.selectionId)
                          }}
                          disabled={stripeStatusLoading}
                        >
                          {stripeActionLabel}
                        </button>
                      )}
                      {showMeeting && (
                        <button
                          type="button"
                          className="btn btn-meeting"
                          onClick={(e) => {
                            e.stopPropagation()
                            openMeeting(row.latest.selectionId)
                          }}
                        >
                          Meeting
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
        {statusFilter === 'clients' && clientCursor && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={loadMoreClients}
              disabled={clientLoadingMore}
            >
              {clientLoadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </section>
  )

  const showHeaderCtas = ready && !loading && !needsProviderProfile
  const pageHead = (
    <div className="page-head">
	      <div>
	        <p className="hero-kicker">Professionals</p>
	        <h1>Dashboard</h1>
	        <p className="hint">Manage bookings, notes, payments, and message clients from one place.</p>
	      </div>
      {showHeaderCtas && (
        <div className="inline-gap-2">
          <button className="btn btn-calendar" type="button" onClick={() => setAddBookingOpen(true)}>
            <CalendarPlus size={18} strokeWidth={1.6} aria-hidden="true" />
            Add Booking
          </button>
          <Link className="btn btn-calendar" to="/calendar">
            <Calendar size={18} strokeWidth={1.6} aria-hidden="true" />
            Go to Calendar
          </Link>
        </div>
      )}
    </div>
  )
  const activeNotesItem = notesActiveId
    ? notesItemsFiltered.find((item) => item.selectionId === notesActiveId) || null
    : null
  const activeNotesKey = activeNotesItem ? activeNotesItem.selectionId : null
  const activeNotesValue = activeNotesKey ? (notesDrafts[activeNotesKey] ?? '') : ''
  const activeNotesSavedAt = activeNotesKey ? notesSavedAt[activeNotesKey] : null
  const activeNotesSaving = activeNotesKey ? notesSaving[activeNotesKey] : false
  const activeNotesPublishedAt = activeNotesItem?.notesPublishedAt || null
  const activeNotesPublished = Boolean(activeNotesPublishedAt)
  const activeNotesAttachments = activeNotesItem ? normalizeAttachments(activeNotesItem) : []
  const notesPaymentEligible = activeNotesItem
    ? ['pending', 'confirmed'].includes(categorizeItem(activeNotesItem))
    : false
  const reviewActiveItem = reviewSelectionId
    ? items.find((item) => item.selectionId === reviewSelectionId) || null
    : null
  const reviewDetails =
    reviewActiveItem?.reviewReceived && reviewActiveItem.reviewReceived.submitted
      ? reviewActiveItem.reviewReceived
      : null
  React.useEffect(() => {
    if (!reviewOpen) return
    setReviewReplyDraft(String(reviewDetails?.providerReply?.comment || ''))
    setReviewReplyError(null)
    setReviewReplyNotice(null)
  }, [reviewOpen, reviewSelectionId, reviewDetails?.providerReply?.comment])
  const handleSubmitReviewReply = React.useCallback(async () => {
    if (!reviewActiveItem) return
    if (!reviewDetails?.submitted) {
      setReviewReplyError('Client review not found yet.')
      return
    }
    const selectionId = String(reviewActiveItem.selectionId || '').trim()
    const patientId = String(reviewActiveItem.patientId || '').trim()
    if (!selectionId || !patientId) {
      setReviewReplyError('Missing data for this reply.')
      return
    }
    const comment = reviewReplyDraft.trim()
    if (!comment) {
      setReviewReplyError('Type a reply before posting.')
      return
    }
    setReviewReplyBusy(true)
    setReviewReplyError(null)
    setReviewReplyNotice(null)
    try {
      const res = await api.replyToSpecialistReview(activeStack, {
        selectionId,
        patientId,
        comment,
      })
      const providerReply = {
        comment,
        ...(res?.providerReply || {}),
      }
      setItems((prev) =>
        prev.map((item) => {
          if (item.selectionId !== selectionId) return item
          return {
            ...item,
            reviewReceived: {
              ...(item.reviewReceived || {}),
              submitted: true,
              providerReply,
            },
          }
        })
      )
      setReviewReplyDraft(comment)
      setReviewReplyNotice('Reply saved. It is now live on your public EUCA Match profile, and the client was notified by email.')
    } catch (err: any) {
      setReviewReplyError(err?.message || 'Failed to save reply.')
    } finally {
      setReviewReplyBusy(false)
    }
  }, [reviewActiveItem, reviewDetails?.submitted, reviewReplyDraft, activeStack])
  const reviewReminderActiveItem = reviewReminderSelectionId
    ? items.find((item) => item.selectionId === reviewReminderSelectionId) || null
    : null
  const activeNotesEditorValue = activeNotesPublished ? activeNotesValue : renderNoteHtml(activeNotesValue)
  const renderNotesDraftPanel = React.useCallback((selectionId: string) => {
    if (!selectionId) return null
    const item = items.find((entry) => entry.selectionId === selectionId)
    if (item?.notesPublishedAt) return null
    const sourceText = getDraftSourceText(selectionId)
    const isGenerating = Boolean(notesDraftGenerating[selectionId])
    const error = notesDraftErrors[selectionId] || null
    const sourceLength = sourceText.length
    const shownLength = Math.min(sourceLength, MAX_AI_DRAFT_INPUT_CHARS)
    const isTrimmed = sourceLength > MAX_AI_DRAFT_INPUT_CHARS
    return (
      <details className="notes-ai-card notes-ai-draft" key={`draft-${selectionId}`}>
        <summary>
          <span className="notes-ai-title"><Wand2 size={16} />AI draft note</span>
        </summary>
        <div className="notes-ai-body">
          {error && <div className="banner error">{error}</div>}
          <p className="hint">Add a few quick points to your note, then click on Draft to shape a structured note and get smart suggestions and prompt directions.</p>
          <div className="notes-ai-meta">
            Using {shownLength}/{MAX_AI_DRAFT_INPUT_CHARS} characters from your notes.
            {isTrimmed && (
              <span className="notes-ai-disclaimer">Long notes are trimmed to the first {MAX_AI_DRAFT_INPUT_CHARS} characters.</span>
            )}
            <span className="notes-ai-disclaimer">AI drafts are designed to exclude personal identifiers.</span>
          </div>
          <div className="notes-ai-actions">
            <button
              type="button"
              className="btn btn-primary btn-compact"
              onClick={() => generateNotesDraft(selectionId)}
              disabled={isGenerating || !sourceText}
            >
              {isGenerating ? (
                <>
                  <span className="btn-spinner" />
                  Drafting…
                </>
              ) : 'Draft'}
            </button>
            {!sourceText && (
              <span className="hint">Add some notes first to enable drafting.</span>
            )}
          </div>
        </div>
      </details>
    )
  }, [
    items,
    notesDraftGenerating,
    notesDraftErrors,
    generateNotesDraft,
    getDraftSourceText,
  ])
  const renderNotesDraftPreview = React.useCallback((selectionId: string) => {
    if (!selectionId) return null
    const item = items.find((entry) => entry.selectionId === selectionId)
    if (item?.notesPublishedAt) return null
    const draftHtml = notesDraftPreview[selectionId]
    if (!draftHtml) return null
    return (
      <div className="notes-ai-draft-preview">
        <div className="notes-ai-draft-header">Draft note (preview)</div>
        <div
          className="notes-ai-draft-body"
          dangerouslySetInnerHTML={{ __html: renderNoteHtml(draftHtml) || '<p>—</p>' }}
        />
        <div className="notes-ai-actions">
          <button
            type="button"
            className="btn btn-primary btn-compact"
            onClick={() => applyNotesDraft(selectionId, 'replace')}
          >
            Replace notes
          </button>
          <button
            type="button"
            className="btn btn-outline btn-compact"
            onClick={() => applyNotesDraft(selectionId, 'add')}
          >
            Add to notes
          </button>
          <button
            type="button"
            className="btn btn-outline btn-compact"
            onClick={() => discardNotesDraft(selectionId)}
          >
            Discard
          </button>
        </div>
      </div>
    )
  }, [items, notesDraftPreview, renderNoteHtml, applyNotesDraft, discardNotesDraft])
  const renderNotesSummaryPanel = React.useCallback((selectionId: string) => {
    if (!selectionId) return null
    const summaryItem = items.find((entry) => entry.selectionId === selectionId)
    const savedNotesValue = summaryItem?.notes || ''
    const draftNotesValue = notesDrafts[selectionId] ?? savedNotesValue
    const notesDirty =
      normalizeNoteText(draftNotesValue) !== normalizeNoteText(savedNotesValue)
    const summaryValue = notesSummaryDrafts[selectionId] ?? ''
    const savedAt = notesSummarySavedAt[selectionId] || null
    const isEditing = Boolean(notesSummaryEditing[selectionId])
    const isGenerating = Boolean(notesSummaryGenerating[selectionId])
    const isSaving = Boolean(notesSummarySaving[selectionId])
    const error = notesSummaryErrors[selectionId] || null
    const hasSummary = Boolean(summaryValue && summaryValue.trim())
    const actionLabel = hasSummary ? 'Regenerate' : 'Generate'

    return (
      <details className="notes-ai-card" key={`ai-${selectionId}`}>
        <summary>
          <span className="notes-ai-title"><Sparkles size={16} />AI-assisted summary</span>
        </summary>
        <div className="notes-ai-body">
          {error && <div className="banner error">{error}</div>}
          {notesDirty && (
            <div className="hint warn">Save notes to include your latest changes in the AI summary.</div>
          )}
          {isEditing ? (
            <textarea
              className="input notes-ai-textarea"
              rows={6}
              maxLength={MAX_AI_SUMMARY_CHARS}
              value={summaryValue}
              onChange={(e) => updateNotesSummaryDraft(selectionId, e.target.value)}
              placeholder="Generate a summary from your saved notes..."
            />
          ) : hasSummary ? (
            <div className="notes-ai-text">{summaryValue}</div>
          ) : (
            <div className="hint">No summary yet. Generate from your saved notes.</div>
          )}
          <div className="notes-ai-actions">
            <button
              type="button"
              className="btn btn-outline btn-compact"
              onClick={() => generateNotesSummary(selectionId)}
              disabled={isGenerating || isSaving}
            >
              {isGenerating ? 'Generating…' : actionLabel}
            </button>
            {!isEditing && hasSummary && (
              <button
                type="button"
                className="btn btn-outline btn-compact"
                onClick={() => setNotesSummaryEditing((prev) => ({ ...prev, [selectionId]: true }))}
                disabled={isGenerating || isSaving}
              >
                Edit
              </button>
            )}
            {isEditing && (
              <button
                type="button"
                className="btn btn-primary btn-compact"
                onClick={() => saveNotesSummary(selectionId, summaryValue)}
                disabled={isGenerating || isSaving}
              >
                {isSaving ? 'Saving…' : 'Save'}
              </button>
            )}
            {isEditing && (
              <button
                type="button"
                className="btn btn-outline btn-compact"
                onClick={() => cancelNotesSummaryEdit(selectionId)}
                disabled={isGenerating || isSaving}
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline btn-compact"
              onClick={() => saveNotesSummary(selectionId, '')}
              disabled={!hasSummary || isGenerating || isSaving}
            >
              Delete
            </button>
          </div>
          <div className="notes-ai-meta">
            {savedAt ? `Updated ${new Date(savedAt).toLocaleString()}` : 'AI summaries are private to you.'}
            <span className="notes-ai-disclaimer">AI summaries are designed to exclude personal identifiers.</span>
          </div>
        </div>
      </details>
    )
  }, [
    items,
    notesDrafts,
    normalizeNoteText,
    notesSummaryDrafts,
    notesSummarySavedAt,
    notesSummaryEditing,
    notesSummaryGenerating,
    notesSummarySaving,
    notesSummaryErrors,
    generateNotesSummary,
    saveNotesSummary,
    cancelNotesSummaryEdit,
    updateNotesSummaryDraft,
  ])
  const renderAttachments = (attachments: NoteAttachment[]) => {
    if (!attachments.length) return null
    const images = attachments.filter((att) => isImageAttachment(att))
    const files = attachments.filter((att) => !isImageAttachment(att))
    return (
      <div className="notes-attachments">
        {images.length > 0 && (
          <div>
            <div className="field-label">Images</div>
            <div className="notes-image-grid">
              {images.map((att) => {
                const url = notesAttachmentUrls[att.key] || ''
                return (
                  <a
                    key={att.key}
                    href={url || '#'}
                    className="notes-image-card"
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => {
                      if (!url) e.preventDefault()
                    }}
                  >
                    {url ? (
                      <img src={url} alt={att.name || 'attachment'} loading="lazy" />
                    ) : (
                      <div className="notes-image-placeholder">Image uploading...</div>
                    )}
                  </a>
                )
              })}
            </div>
          </div>
        )}
        {files.length > 0 && (
          <div>
            <div className="field-label">Files</div>
            <div className="notes-file-list">
              {files.map((att) => {
                const url = notesAttachmentUrls[att.key] || ''
                const metaParts = [formatBytes(att.size), att.type].filter(Boolean)
                return (
                  <a
                    key={att.key}
                    href={url || '#'}
                    className="notes-file-card"
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => {
                      if (!url) e.preventDefault()
                    }}
                  >
                    <div className="notes-file-name">{att.name || att.key.split('/').pop() || 'Attachment'}</div>
                    <div className="notes-file-meta">{metaParts.join(' - ') || 'File'}</div>
                  </a>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }
  const notesOverlay = notesOpen && (
    <div className="overlay show inbox-overlay" role="dialog" aria-modal="true" onClick={closeNotes}>
      <div className="sheet inbox-notes-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-header">
          <div>
            <div className="sheet-title">Notes</div>
            {notesClientName && (
              <div className="hint" style={{ marginTop: '.25rem' }}>
                {notesClientName}
              </div>
            )}
          </div>
          <div className="inline-gap-2">
            {activeNotesItem && (
              <button
                type="button"
                className="btn btn-details"
                onClick={() => {
                  closeNotes()
                  openView(activeNotesItem.selectionId)
                }}
              >
                Client details
              </button>
            )}
            {activeNotesItem && notesPaymentEligible && (
              <button
                type="button"
                className="btn btn-payment"
                onClick={() => {
                  closeNotes()
                  handlePaymentAction(activeNotesItem.selectionId)
                }}
                disabled={stripeStatusLoading}
              >
                {stripeActionLabel}
              </button>
            )}
            {activeNotesItem && notesPaymentEligible && (
              <button
                type="button"
                className="btn btn-meeting"
                onClick={() => {
                  closeNotes()
                  openMeeting(activeNotesItem.selectionId)
                }}
              >
                Meeting
              </button>
            )}
            <button type="button" className="btn btn-outline" onClick={closeNotes}>Close</button>
          </div>
        </div>
        <div className="sheet-body">
          {notesError && <div className="banner error" style={{ marginBottom: '1rem' }}>{notesError}</div>}
          {!notesItemsFiltered.length && (
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <p>No appointments yet.</p>
              <p className="hint">Notes will appear once you receive requests.</p>
            </div>
          )}
          {!!notesItemsFiltered.length && (
            <>
              <div className="inbox-notes-layout">
                <div className="inbox-notes-list" role="tablist" aria-label="Appointments">
                  {notesItemsFiltered.map((item) => {
                    const active = item.selectionId === notesActiveId
                    return (
                      <button
                        key={item.selectionId}
                        type="button"
                        className={`inbox-notes-item ${active ? 'active' : ''}`}
                        onClick={() => setNotesActiveId(item.selectionId)}
                      >
                        <div className="inbox-notes-title">Booking</div>
                        <div className="inbox-notes-meta">{notesTimeLabels[item.selectionId] || '—'}</div>
                      </button>
                    )
                  })}
                </div>
                <div className="inbox-notes-editor">
                  <div className="inbox-notes-main">
                    {activeNotesItem ? (
                      <>
                        {!activeNotesPublished ? (
                          <>
                            {renderNotesDraftPreview(activeNotesItem.selectionId)}
                            <NotesRichEditor
                              value={activeNotesEditorValue}
                              placeholder="Add notes for this booking..."
                              onChange={(html) => updateNotesDraft(
                                activeNotesItem.selectionId,
                                activeNotesKey || activeNotesItem.selectionId,
                                html
                              )}
                              onImageUpload={async (file) => {
                                const res = await uploadNotesAttachment(activeNotesItem.selectionId, file, 'image')
                                if (!res?.url) return null
                                return { url: res.url, key: res.attachment.key, name: res.attachment.name || file.name }
                              }}
                              onAttachmentUpload={async (file) => {
                                await uploadNotesAttachment(activeNotesItem.selectionId, file, 'file')
                              }}
                            />
                            {renderAttachments(activeNotesAttachments)}
                            <div className="inbox-notes-actions">
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => saveNotes(
                                  activeNotesItem.selectionId,
                                  activeNotesKey || activeNotesItem.selectionId,
                                  activeNotesValue,
                                  activeNotesAttachments.length ? { attachments: activeNotesAttachments } : undefined
                                )}
                                disabled={activeNotesSaving}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => saveNotes(
                                  activeNotesItem.selectionId,
                                  activeNotesKey || activeNotesItem.selectionId,
                                  activeNotesValue,
                                  {
                                    published: true,
                                    attachments: activeNotesAttachments.length ? activeNotesAttachments : undefined,
                                  }
                                )}
                                disabled={activeNotesSaving || !activeNotesValue.trim()}
                              >
                                Publish
                              </button>
                              <div className="inbox-notes-saved">
                                {activeNotesSaving
                                  ? 'Saving...'
                                  : activeNotesSavedAt
                                    ? `Saved ${new Date(activeNotesSavedAt).toLocaleTimeString()}`
                                    : 'Not saved yet'}
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            {renderNotesDraftPreview(activeNotesItem.selectionId)}
                            <div
                              className="inbox-notes-preview"
                              dangerouslySetInnerHTML={{
                                __html: renderNoteHtml(activeNotesValue) || '<p>—</p>',
                              }}
                            />
                            {renderAttachments(activeNotesAttachments)}
                            <div className="inbox-notes-published-row">
                              <div className="inbox-notes-published">
                                Published {fmtPublishedAt(activeNotesPublishedAt)}
                              </div>
                              <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => saveNotes(
                                  activeNotesItem.selectionId,
                                  activeNotesKey || activeNotesItem.selectionId,
                                  activeNotesValue,
                                  {
                                    published: false,
                                    attachments: activeNotesAttachments.length ? activeNotesAttachments : undefined,
                                  }
                                )}
                              >
                                Unpublish
                              </button>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <p className="hint">Select an appointment to view notes.</p>
                    )}
                  </div>
                  {activeNotesItem && (
                    <aside className="notes-ai-panel">
                      {renderNotesDraftPanel(activeNotesItem.selectionId)}
                      {renderNotesSummaryPanel(activeNotesItem.selectionId)}
                    </aside>
                  )}
                </div>
              </div>

              <div className="inbox-notes-accordion" aria-label="Booking notes">
                {notesItemsFiltered.map((item) => {
                  const notesKey = item.selectionId
                  const value = notesDrafts[notesKey] ?? ''
                  const savedAt = notesSavedAt[notesKey]
                  const saving = notesSaving[notesKey]
                  const publishedAt = item.notesPublishedAt || null
                  const isPublished = Boolean(publishedAt)
                  const attachments = normalizeAttachments(item)
                  return (
                    <details key={item.selectionId} className="inbox-notes-fold">
                      <summary>
                        <div>
                          <div className="inbox-notes-title">Booking</div>
                          <div className="inbox-notes-meta">{notesTimeLabels[item.selectionId] || '—'}</div>
                        </div>
                        <span className="notes-chevron">^</span>
                      </summary>
                      <div className="inbox-notes-panel">
                        {!isPublished ? (
                          <>
                            {renderNotesDraftPreview(item.selectionId)}
                            <NotesRichEditor
                              value={renderNoteHtml(value)}
                              placeholder="Add notes for this booking..."
                              onChange={(html) => updateNotesDraft(item.selectionId, notesKey, html)}
                              onImageUpload={async (file) => {
                                const res = await uploadNotesAttachment(item.selectionId, file, 'image')
                                if (!res?.url) return null
                                return { url: res.url, key: res.attachment.key, name: res.attachment.name || file.name }
                              }}
                              onAttachmentUpload={async (file) => {
                                await uploadNotesAttachment(item.selectionId, file, 'file')
                              }}
                            />
                            {renderAttachments(attachments)}
                            <div className="inbox-notes-actions">
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => saveNotes(
                                  item.selectionId,
                                  notesKey,
                                  value,
                                  attachments.length ? { attachments } : undefined
                                )}
                                disabled={saving}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => saveNotes(item.selectionId, notesKey, value, {
                                  published: true,
                                  attachments: attachments.length ? attachments : undefined,
                                })}
                                disabled={saving || !value.trim()}
                              >
                                Publish
                              </button>
                              <div className="inbox-notes-saved">
                                {saving
                                  ? 'Saving...'
                                  : savedAt
                                    ? `Saved ${new Date(savedAt).toLocaleTimeString()}`
                                    : 'Not saved yet'}
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            {renderNotesDraftPreview(item.selectionId)}
                            <div
                              className="inbox-notes-preview"
                              dangerouslySetInnerHTML={{ __html: renderNoteHtml(value) || '<p>—</p>' }}
                            />
                            {renderAttachments(attachments)}
                            <div className="inbox-notes-published-row">
                              <div className="inbox-notes-published">
                                Published {fmtPublishedAt(publishedAt)}
                              </div>
                              <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => saveNotes(item.selectionId, notesKey, value, {
                                  published: false,
                                  attachments: attachments.length ? attachments : undefined,
                                })}
                              >
                                Unpublish
                              </button>
                            </div>
                          </>
                        )}
                        {renderNotesDraftPanel(item.selectionId)}
                        {renderNotesSummaryPanel(item.selectionId)}
                      </div>
                    </details>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
  const viewOverlay = viewOpen && viewSelectionId && (
    <div className="overlay show inbox-overlay" role="dialog" aria-modal="true" onClick={closeView}>
      <div className="sheet inbox-view-sheet" onClick={(e) => e.stopPropagation()}>
        <InboxDetailContent
          selectionId={viewSelectionId}
          initialStack={activeStack}
          inline
          onClose={closeView}
          onOpenNotes={openNotesFromView}
          onOpenPayment={openPayment}
          onOpenMeeting={openMeeting}
          onOpenReview={openReview}
          onOpenReviewReminder={openReviewReminder}
        />
      </div>
    </div>
  )
  const paymentOverlay = paymentOpen && paymentSelectionId && (
    <div className="overlay show inbox-overlay" role="dialog" aria-modal="true" onClick={closePayment}>
      <div className="sheet inbox-view-sheet" onClick={(e) => e.stopPropagation()}>
        <InboxDetailContent
          selectionId={paymentSelectionId}
          initialStack={activeStack}
          inline
          mode="payment"
          onClose={closePayment}
          onOpenNotes={openNotes}
          onOpenDetails={openView}
          onOpenMeeting={openMeeting}
          onOpenReview={openReview}
          onOpenReviewReminder={openReviewReminder}
        />
      </div>
    </div>
  )
  const meetingOverlay = meetingOpen && meetingSelectionId && (
    <div className="overlay show inbox-overlay" role="dialog" aria-modal="true" onClick={closeMeeting}>
      <div className="sheet inbox-view-sheet" onClick={(e) => e.stopPropagation()}>
        <InboxDetailContent
          selectionId={meetingSelectionId}
          initialStack={activeStack}
          inline
          mode="meeting"
          onClose={closeMeeting}
          onOpenNotes={openNotes}
          onOpenDetails={openView}
          onOpenPayment={openPayment}
          onOpenReview={openReview}
          onOpenReviewReminder={openReviewReminder}
        />
      </div>
    </div>
  )
  const reviewOverlay = reviewOpen && (
    <div className="overlay show inbox-overlay" role="dialog" aria-modal="true" onClick={closeReview}>
      <div className="sheet inbox-view-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-header">
          <div>
            <div className="sheet-title">Client review</div>
            {reviewActiveItem && (
              <div className="hint" style={{ marginTop: '.25rem' }}>
                {(reviewActiveItem.patientName || reviewActiveItem.patientContact?.name || 'Client')}{' · '}
                {fmtWhenLocal(reviewActiveItem.slotISO, reviewActiveItem.createdAt, reviewActiveItem.selectionCreatedAt)}
              </div>
            )}
          </div>
          <div className="inline-gap-2">
            {reviewActiveItem && (
              <button
                type="button"
                className="btn btn-details"
                onClick={() => {
                  closeReview()
                  openView(reviewActiveItem.selectionId)
                }}
              >
                Client details
              </button>
            )}
            <button type="button" className="btn btn-outline" onClick={closeReview}>Close</button>
          </div>
        </div>
        <div className="sheet-body">
          {reviewDetails ? (
            <div className="review-card">
              <div className="review-meta">
                <div className="review-from">
                  {reviewDetails.reviewerFirstName ? `From ${reviewDetails.reviewerFirstName}` : 'From client'}
                </div>
                <div className="hint">Received {fmtReviewAt(reviewDetails.createdAt)}</div>
              </div>
              <RatingStarsDisplay value={reviewDetails.rating || 0} showValue />
              {reviewDetails.comment ? (
                <div className="review-comment">{reviewDetails.comment}</div>
              ) : (
                <div className="hint">No written feedback.</div>
              )}
              {reviewDetails.providerReply?.comment ? (
                <div className="review-provider-reply">
                  <div className="hint" style={{ marginBottom: 4 }}>
                    {reviewDetails.providerReply?.replierName || 'Professional'} replied
                  </div>
                  <div className="review-comment" style={{ marginTop: 0 }}>
                    {reviewDetails.providerReply.comment}
                  </div>
                </div>
              ) : null}
              <div className="review-reply-panel">
                <div className="detail-label">
                  {reviewDetails.providerReply?.comment ? 'Update your reply' : 'Reply to this review'}
                </div>
                <p className="hint" style={{ marginTop: 4 }}>
                  Your reply is shown on your public EUCA Match profile.
                </p>
                <textarea
                  className="input"
                  rows={4}
                  placeholder="Write a short professional reply"
                  value={reviewReplyDraft}
                  onChange={(e) => {
                    setReviewReplyDraft(e.target.value)
                    setReviewReplyError(null)
                    setReviewReplyNotice(null)
                  }}
                  disabled={reviewReplyBusy}
                />
                {reviewReplyError && <div className="field-help">{reviewReplyError}</div>}
                {reviewReplyNotice ? (
                  <div className="hint" style={{ color: 'var(--success)' }}>
                    {reviewReplyNotice}
                  </div>
                ) : null}
                <div className="inline-gap-2" style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSubmitReviewReply}
                    disabled={reviewReplyBusy}
                  >
                    {reviewReplyBusy ? 'Saving…' : reviewDetails.providerReply?.comment ? 'Update reply' : 'Post reply'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="banner info">No client review received yet.</div>
          )}
        </div>
      </div>
    </div>
  )
  const reviewReminderOverlay = reviewReminderOpen && (
    <div className="overlay show inbox-overlay" role="dialog" aria-modal="true" onClick={closeReviewReminder}>
      <div className="sheet inbox-view-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-header">
          <div>
            <div className="sheet-title">Send review reminder</div>
            {reviewReminderActiveItem && (
              <div className="hint" style={{ marginTop: '.25rem' }}>
                {(reviewReminderActiveItem.patientName || reviewReminderActiveItem.patientContact?.name || 'Client')}{' · '}
                {fmtWhenLocal(
                  reviewReminderActiveItem.slotISO,
                  reviewReminderActiveItem.createdAt,
                  reviewReminderActiveItem.selectionCreatedAt,
                )}
              </div>
            )}
          </div>
          <div className="inline-gap-2">
            {reviewReminderActiveItem && (
              <button
                type="button"
                className="btn btn-details"
                onClick={() => {
                  closeReviewReminder()
                  openView(reviewReminderActiveItem.selectionId)
                }}
              >
                Client details
              </button>
            )}
            <button type="button" className="btn btn-outline" onClick={closeReviewReminder}>Close</button>
          </div>
        </div>
        <div className="sheet-body">
          {reviewReminderSuccess && (
            <div className="banner success">{reviewReminderSuccess}</div>
          )}
          {reviewReminderError && (
            <div className="banner error">{reviewReminderError}</div>
          )}
          <div className="review-reminder-form">
            <div>
              <div className="field-label">Optional message</div>
              <textarea
                className="input"
                rows={4}
                maxLength={500}
                value={reviewReminderMessage}
                onChange={(e) => {
                  setReviewReminderMessage(e.target.value)
                  setReviewReminderDirty(true)
                }}
                placeholder={DEFAULT_REVIEW_REMINDER_MESSAGE}
              />
            </div>
            <div className="review-reminder-footer">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={reviewReminderSaveDefault}
                  onChange={(e) => setReviewReminderSaveDefault(e.target.checked)}
                />
                <span>Save as default message</span>
              </label>
              <button
                type="button"
                className="btn btn-primary"
                onClick={sendReviewReminder}
                disabled={reviewReminderSending}
              >
                {reviewReminderSending ? 'Sending…' : 'Send reminder'}
              </button>
            </div>
            <div className="hint">Limit: 1 reminder per booking per week, max 3 total.</div>
          </div>
        </div>
      </div>
    </div>
  )
  const addBookingOverlay = (
    <AddBookingSheet
      open={addBookingOpen}
      stack={activeStack}
      onClose={() => setAddBookingOpen(false)}
      onCreated={() => load()}
    />
  )

  if (!ready || loading) {
    return (
      <div className="container page-wide inbox-page">
        {pageHead}
        <Spinner label="Loading dashboard…" />
        {notesOverlay}
        {viewOverlay}
        {paymentOverlay}
        {meetingOverlay}
        {reviewOverlay}
        {reviewReminderOverlay}
        {addBookingOverlay}
      </div>
    )
  }

  if (needsProviderProfile) {
    return (
      <div className="container page-wide inbox-page">
        {pageHead}
        {renderStackSwitcher()}
        <section className="card section-card">
          <h3>Complete your professional registration</h3>
          <p className="hint">
            Finish your registration to start receiving client requests.
          </p>
          <Link className="btn btn-primary" to="/professionals" style={{ marginTop: '.75rem' }}>
            Complete registration
          </Link>
        </section>
        {notesOverlay}
        {viewOverlay}
        {paymentOverlay}
        {meetingOverlay}
        {reviewOverlay}
        {reviewReminderOverlay}
        {addBookingOverlay}
      </div>
    )
  }

  return (
    <div className="container page-wide inbox-page">
      {pageHead}
      {renderStackSwitcher()}
      {renderRequests()}
      {notesOverlay}
      {viewOverlay}
      {paymentOverlay}
      {meetingOverlay}
      {reviewOverlay}
      {reviewReminderOverlay}
      {addBookingOverlay}
    </div>
  )
}
